import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePowerHistory, kwh, money, type HistoryRange } from '@/lib/power'
import { cn } from '@/lib/utils'

const RANGES: { key: HistoryRange; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
]

function fmtBucket(b: string, gran: 'hour' | 'day' | 'month'): string {
  // bucket is "YYYY-MM" (month), "YYYY-MM-DD" (day) or "YYYY-MM-DDTHH:00" (hour)
  if (gran === 'hour') return b.slice(11, 16)
  if (gran === 'month') {
    const d = new Date(b + '-01T00:00:00')
    return d.toLocaleDateString([], { month: 'short' })
  }
  const d = new Date(b + 'T00:00:00')
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' })
}

export function PowerHistoryCard() {
  const [range, setRange] = useState<HistoryRange>('month')
  const { data, isLoading } = usePowerHistory(range)

  const gran = data?.granularity ?? 'day'
  const rows = (data?.points ?? []).map((p) => ({
    label: fmtBucket(p.bucket, gran),
    HC: p.kwh_hc,
    HP: p.kwh_hp,
    cost: p.cost,
    kwh: p.kwh,
  }))
  const cur = data?.currency ?? 'EUR'

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">Consumption history</CardTitle>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={cn(
                'rounded-md px-2 py-1 text-xs transition-colors',
                range === r.key
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <Skeleton className="h-64" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">
            No data collected for this period yet.
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-4 mb-3 text-sm">
              <span className="text-muted-foreground">
                Total: <span className="text-foreground font-semibold">{kwh(data.total_kwh)}</span>
              </span>
              <span className="text-muted-foreground">
                Cost:{' '}
                <span className="text-foreground font-semibold">{money(data.total_cost, cur)}</span>
              </span>
            </div>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    width={44}
                    tickFormatter={(v: number) => `${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [`${Number(value).toFixed(2)} kWh`, String(name)]}
                    labelFormatter={(label, payload) => {
                      const c = (payload?.[0]?.payload as { cost?: number } | undefined)?.cost
                      return `${label}${c != null ? ` — ${money(c, cur)}` : ''}`
                    }}
                  />
                  <Bar dataKey="HC" stackId="e" fill="hsl(var(--primary))" fillOpacity={0.5} />
                  <Bar dataKey="HP" stackId="e" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-primary" /> HP
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-primary/50" /> HC
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
